import type { Transaction } from 'xrpl/dist/npm'
import { FARMS_ACTION_FEE_DROPS, FARMS_API_BASE, FARMS_ESCROW_ADDRESS, FARMS_SIGNAL_ADDRESS } from './config'
import { XrplSubmitResult } from './types'

export type FarmItem = {
  id: string
  name: string
  apr?: number
  multiplier?: string
  liquidity?: number
  liquidityUsd?: number | null
  liquidityXrp?: number | null
  stakedBalance?: string
  earned?: string
  totalStaked?: string
  rewardSymbol?: string
  rewardPriceUsd?: number | null
  stakingSymbol?: string
  lpTokenCurrency?: string | null
  lpTokenIssuer?: string | null
  lpTokenSymbol?: string | null
  lpTokenSupply?: string | null
  tradingFee?: number | null
  rewardPerDay?: number
  isFinished?: boolean
  isArchived?: boolean
}

export type FarmActionResult = {
  txHash: string
  result?: any
}

export type FarmClaimResult = {
  txHash: string
  claimed: string
  rewardSymbol?: string
}

type FarmSignalParams = {
  farmId: string
  account: string
  amount?: string
  signAndSubmit: (txjson: Transaction) => Promise<XrplSubmitResult>
}

type FarmStakeParams = {
  farmId: string
  account: string
  amount: string
  lpTokenCurrency: string
  lpTokenIssuer: string
  signAndSubmit: (txjson: Transaction) => Promise<XrplSubmitResult>
}

const getUrl = (path: string) => {
  const base = FARMS_API_BASE || '/api'
  return `${base}${path}`
}

const encodeHex = (value: string) => {
  const bytes = new TextEncoder().encode(value)
  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()
}

const buildSignalTx = (account: string, action: string, farmId: string, amount?: string): Transaction => {
  const destination = FARMS_SIGNAL_ADDRESS || FARMS_ESCROW_ADDRESS
  if (!destination) {
    throw new Error('Missing farm signal address')
  }
  const payload = JSON.stringify({ action, farmId, amount })
  return {
    TransactionType: 'Payment',
    Account: account,
    Destination: destination,
    Amount: FARMS_ACTION_FEE_DROPS,
    Memos: [
      {
        Memo: {
          MemoType: encodeHex('FARM'),
          MemoData: encodeHex(payload),
        },
      },
    ],
  }
}

const buildStakeTx = (
  account: string,
  farmId: string,
  amount: string,
  lpTokenCurrency: string,
  lpTokenIssuer: string,
): Transaction => {
  if (!FARMS_ESCROW_ADDRESS) {
    throw new Error('Missing farm escrow address')
  }
  if (!lpTokenCurrency || !lpTokenIssuer) {
    throw new Error('Missing LP token details')
  }
  const payload = JSON.stringify({ action: 'stake', farmId, amount })
  return {
    TransactionType: 'Payment',
    Account: account,
    Destination: FARMS_ESCROW_ADDRESS,
    Amount: {
      currency: lpTokenCurrency,
      issuer: lpTokenIssuer,
      value: amount,
    },
    Memos: [
      {
        Memo: {
          MemoType: encodeHex('FARM'),
          MemoData: encodeHex(payload),
        },
      },
    ],
  }
}

export const fetchFarms = async (account?: string): Promise<FarmItem[]> => {
  const query = account ? `?account=${encodeURIComponent(account)}` : ''
  const response = await fetch(getUrl(`/farms${query}`))
  if (!response.ok) {
    throw new Error('Failed to fetch farms')
  }
  const data = await response.json()
  return Array.isArray(data) ? data : data?.farms || []
}

const submitSignalAction = async (
  endpoint: string,
  { farmId, account, amount, signAndSubmit }: FarmSignalParams,
): Promise<FarmActionResult> => {
  const txjson = buildSignalTx(account, endpoint, farmId, amount)
  const result = await signAndSubmit(txjson)
  const response = await fetch(getUrl(`/farms/${endpoint}`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ farmId, account, amount, txHash: result.hash }),
  })
  if (!response.ok) {
    let detail = ''
    try {
      const payload = await response.json()
      detail = payload?.error || ''
    } catch (error) {
      detail = ''
    }
    throw new Error(detail || `Failed to ${endpoint}`)
  }
  return { txHash: result.hash, result: await response.json() }
}

export const stakeFarm = async ({ farmId, account, amount, lpTokenCurrency, lpTokenIssuer, signAndSubmit }: FarmStakeParams) => {
  const txjson = buildStakeTx(account, farmId, amount, lpTokenCurrency, lpTokenIssuer)
  const result = await signAndSubmit(txjson)
  const response = await fetch(getUrl('/farms/stake'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ farmId, account, amount, txHash: result.hash }),
  })
  if (!response.ok) {
    let detail = ''
    try {
      const payload = await response.json()
      detail = payload?.error || ''
    } catch (error) {
      detail = ''
    }
    throw new Error(detail || 'Failed to stake')
  }
  return { txHash: result.hash, result: await response.json() }
}

export const unstakeFarm = async (params: FarmSignalParams) => submitSignalAction('unstake', params)

export const claimFarm = async ({
  farmId,
  account,
  signAndSubmit,
}: Omit<FarmSignalParams, 'amount'>): Promise<FarmClaimResult> => {
  const txjson = buildSignalTx(account, 'claim', farmId)
  const result = await signAndSubmit(txjson)
  const response = await fetch(getUrl('/farms/claim'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ farmId, account, txHash: result.hash }),
  })
  if (!response.ok) {
    let detail = ''
    try {
      const payload = await response.json()
      detail = payload?.error || ''
    } catch (error) {
      detail = ''
    }
    throw new Error(detail || 'Failed to claim farm')
  }
  const payload = await response.json()
  return { txHash: result.hash, ...payload }
}
